// src/components/TenantAgreementModalView.jsx
import React, { useState, useEffect }  from 'react';
import { Document, Page, Text, View, Image, StyleSheet, Font, PDFViewer, PDFDownloadLink, pdf } from '@react-pdf/renderer';
import { generateAgreementPDF, getAgreementJsonData } from '../apis/services/pgApi';

// ─────────────────────────────────────────────────────────────────
// LETTERHEAD ARTWORK
// ─────────────────────────────────────────────────────────────────
const LETTERHEAD_FIRST = '/assets/letterhead-first.png';

// ─────────────────────────────────────────────────────────────────
// FONT REGISTRATION
// ─────────────────────────────────────────────────────────────────
Font.register({
  family: 'NotoSans',
  fonts: [
    { src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Regular.ttf', fontWeight: 'normal' },
    { src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Bold.ttf', fontWeight: 'bold' },
  ],
});

// ═════════════════════════════════════════════════════════════════
// PDF STYLES
// ═════════════════════════════════════════════════════════════════

const PAGE_PADDING_TOP = 140;
const PAGE_PADDING_BOTTOM = 85;
const PAGE_PADDING_SIDE = 48;

const HEADER_NAME_TOP = 62;
const HEADER_NAME_LEFT = 48;
const HEADER_NAME_WIDTH = 240;
const HEADER_ROW_LEFT = 340;
const HEADER_ROW_WIDTH = 210;
const HEADER_ROW1_TOP = 70;
const HEADER_ROW2_TOP = 86;
const HEADER_ROW3_TOP = 102;
const FOOTER_TEXT_BOTTOM = 46;

const docStyles = StyleSheet.create({
  page: {
    paddingTop: PAGE_PADDING_TOP,
    paddingBottom: PAGE_PADDING_BOTTOM,
    paddingHorizontal: PAGE_PADDING_SIDE,
    fontSize: 9.5,
    lineHeight: 1.45,
    fontFamily: 'Helvetica',
    color: '#1F2937',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: -1,
  },
  title: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: 1,
  },
  sectionHeading: {
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    marginTop: 10,
    marginBottom: 4,
  },
  bold: { fontFamily: 'Helvetica-Bold' },
  keyword: { fontFamily: 'Helvetica-Bold' },
  headingBold: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10.5,
  },
  subheadingBold: {
    fontFamily: 'Helvetica-Bold',
  },
  textCenterBold: {
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
  },
  clause: { marginBottom: 4, textAlign: 'justify' },
  bulletRow: { flexDirection: 'row', marginBottom: 3 },
  bulletDot: { width: 10 },
  bulletText: { flex: 1, textAlign: 'justify' },
  signBlockWrap: { marginTop: 18 },
  signRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 22 },
  signCol: { width: '46%' },
  signLabel: { fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  signLine: { borderBottomWidth: 1, borderBottomColor: '#374151', marginBottom: 3 },
  signSub: { fontSize: 8.5, color: '#4B5563' },
  witnessWrap: { marginTop: 22 },
  witnessRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 },
  witnessCol: { width: '46%' },
  rupee: {
    fontFamily: 'NotoSans',
    fontWeight: 'bold',
    fontSize: 9.5,
  },
  pgName: {
    position: 'absolute',
    top: HEADER_NAME_TOP,
    left: HEADER_NAME_LEFT,
    width: HEADER_NAME_WIDTH,
    fontSize: 24,
    lineHeight: 1.2,
    fontFamily: 'Helvetica-Bold',
    color: '#111827',
  },
  pgContactRow: {
    position: 'absolute',
    left: HEADER_ROW_LEFT,
    width: HEADER_ROW_WIDTH,
    fontSize: 8,
    lineHeight: 1.25,
    color: '#374151',
  },
  pgFooter: {
    position: 'absolute',
    bottom: FOOTER_TEXT_BOTTOM,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: '#111827',
  },
  verifiedSignature: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    color: '#059669',
  },
  unverifiedSignature: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
});

const Section = ({ children }) => <Text style={docStyles.sectionHeading}>{children}</Text>;
const Clause = ({ children }) => <Text style={docStyles.clause}>{children}</Text>;

const Bullets = ({ items }) => (
  <View>
    {items && items.map((t, i) => (
      <View style={docStyles.bulletRow} key={i}>
        <Text style={docStyles.bulletDot}>•</Text>
        <Text style={docStyles.bulletText}>{typeof t === 'string' ? t : t.Rules || t}</Text>
      </View>
    ))}
  </View>
);

const RuleSection = ({ number, title, intro, items }) => (
  <>
    <Section>{number}. {title}</Section>
    {intro && <Clause>{intro}</Clause>}
    <Bullets items={items} />
  </>
);

const Rupee = () => <Text style={docStyles.rupee}>₹</Text>;

// ─── DATA TRANSFORMATION FUNCTION ──────────────────────────────
const transformAgreementData = (apiData) => {
  if (!apiData) return null;

  // If data is already in the expected format
  if (apiData.pg && !Array.isArray(apiData.pg) && apiData.tenant && !Array.isArray(apiData.tenant)) {
    return apiData;
  }

  // Handle array data from API
  const getFirstItem = (data) => {
    if (Array.isArray(data) && data.length > 0) {
      return data[0];
    }
    return data || {};
  };

  return {
    agreementDate: apiData.agreementDate || '20-07-2026',
    agreementDay: apiData.agreementDay || 'Monday',
    city: apiData.city || 'Delhi',
    state: apiData.state || 'Delhi',
    
    pg: getFirstItem(apiData.pg),
    landlord: getFirstItem(apiData.landlord),
    tenant: getFirstItem(apiData.tenant),
    property: getFirstItem(apiData.property),
    tenancy: getFirstItem(apiData.tenancy),
    rent: getFirstItem(apiData.rent),
    deposit: getFirstItem(apiData.deposit),
    utilities: getFirstItem(apiData.utilities),
    food: getFirstItem(apiData.food),
    rules: getFirstItem(apiData.rules),
    termination: getFirstItem(apiData.termination)
  };
};

// ═════════════════════════════════════════════════════════════════
// RENT AGREEMENT DOCUMENT
// ═════════════════════════════════════════════════════════════════

export const RentAgreementDocument = ({ data, isVerified = false } = {}) => {
  const rawData = data;
  const d = transformAgreementData(rawData);

  const pg = d.pg || {};
  const lo = d.landlord || {};
  const tn = d.tenant || {};
  const pr = d.property || {};
  const tc = d.tenancy || {};
  const rn = d.rent || {};
  const dp = d.deposit || {};
  const ut = d.utilities || {};
  const fd = d.food || {};
  const ru = d.rules || {};
  const tm = d.termination || {};

  let sectionCount = 0;
  const num = () => { sectionCount += 1; return sectionCount; };

  const ruleSections = [
    {
      key: 'houseRules',
      title: 'HOUSE RULES & TENANT OBLIGATIONS',
      intro: 'The Tenant agrees to abide by the following:',
      items: ru.houseRules || [],
    },
    {
      key: 'landlordObligations',
      title: "LANDLORD'S OBLIGATIONS",
      intro: 'The Landlord shall:',
      items: ru.landlordObligations || [],
    },
    {
      key: 'prohibitedActivities',
      title: 'PROHIBITED ACTIVITIES',
      intro: 'The Tenant shall not:',
      items: ru.prohibitedActivities || [],
    },
  ];

  return (
    <Document>
      <Page size="A4" style={docStyles.page} wrap>
        <Image 
          src={LETTERHEAD_FIRST} 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 595.28,
            height: 841.89,
            zIndex: -1,
          }}
          fixed 
        />

        <Text style={docStyles.pgName} fixed>{pg.name || 'AapkaPG'}</Text>
        <Text style={[docStyles.pgContactRow, { top: HEADER_ROW1_TOP }]} fixed>{pg.phone || ''}</Text>
        <Text style={[docStyles.pgContactRow, { top: HEADER_ROW2_TOP }]} fixed>{pg.email || ''}</Text>
        <Text style={[docStyles.pgContactRow, { top: HEADER_ROW3_TOP }]} fixed>{pg.address || ''}</Text>
        <Text style={docStyles.pgFooter} fixed>Thank you for choosing {pg.name || 'AapkaPG'}</Text>

        <Text style={docStyles.title}>RENT AGREEMENT</Text>

        <Clause>
          This Rent Agreement is made and executed on this <Text style={docStyles.keyword}>{d.agreementDay}</Text> <Text style={docStyles.keyword}>{d.agreementDate}</Text> at <Text style={docStyles.keyword}>{d.city}</Text>.
        </Clause>

        <Clause><Text style={docStyles.textCenterBold}>BETWEEN</Text></Clause>

        <Clause>
          <Text style={docStyles.keyword}>{lo.title || 'Mr.'} {lo.name || ''}</Text>
          {lo.parent ? <>, S/o <Text style={docStyles.keyword}>{lo.parent}</Text></> : null}
          {lo.age ? <>, aged <Text style={docStyles.keyword}>{lo.age}</Text> years</> : null},
          residing at <Text style={docStyles.keyword}>{lo.address || ''}</Text>
          {lo.pin ? <>, PIN: <Text style={docStyles.keyword}>{lo.pin}</Text></> : null},
          Mobile: <Text style={docStyles.keyword}>{lo.mobile || ''}</Text>, Email: <Text style={docStyles.keyword}>{lo.email || ''}</Text> (hereinafter
          referred to as the <Text style={docStyles.bold}>"Landlord"</Text> or <Text style={docStyles.bold}>"PG Owner"</Text>, which expression shall, unless repugnant to the
          context, mean and include his/her heirs, executors, administrators and assigns) of the <Text style={docStyles.bold}>ONE PART</Text>;
        </Clause>

        <Clause><Text style={docStyles.textCenterBold}>AND</Text></Clause>

        <Clause>
          <Text style={docStyles.keyword}>{tn.Title || 'Mr.'} {tn.name || ''}</Text>
          {tn.parent ? <>, S/o or D/o <Text style={docStyles.keyword}>{tn.parent}</Text></> : null}
          {tn.Age ? <>, aged <Text style={docStyles.keyword}>{tn.Age}</Text> years</> : null},
          <Text style={docStyles.keyword}>{tn.Occupation || ''}</Text>, residing at <Text style={docStyles.keyword}>{tn.address || ''}</Text>
          {tn.pin ? <>, PIN: <Text style={docStyles.keyword}>{tn.pin}</Text></> : null},
          Mobile: <Text style={docStyles.keyword}>{tn.mobile || ''}</Text>, Email: <Text style={docStyles.keyword}>{tn.email || ''}</Text> (hereinafter
          referred to as the <Text style={docStyles.bold}>"Tenant"</Text>, which expression shall, unless repugnant to the context, mean and include his/her heirs,
          executors, administrators and assigns) of the <Text style={docStyles.bold}>OTHER PART</Text>.
        </Clause>

        <Clause>
          <Text style={docStyles.headingBold}>WHEREAS</Text> the Landlord is the absolute owner of the property situated at <Text style={docStyles.keyword}>{pr.address || ''}</Text>,
          PIN <Text style={docStyles.keyword}>{pr.pin || ''}</Text> (hereinafter referred to as the "PG Premises").
        </Clause>

        {!pr.RoomNmber ? (
          <Clause>
            <Text style={docStyles.headingBold}>WHEREAS</Text> the Tenant has approached the Landlord for paying-guest accommodation
            in <Text style={docStyles.keyword}>{pr.PGName || ''}</Text> for residential purpose only. The specific room, floor, and occupancy
            type shall be allotted solely at the discretion of the Landlord based on availability at the time of check-in, and shall
            be recorded and confirmed separately at the time of such allotment.
          </Clause>
        ) : (
          <Clause>
            <Text style={docStyles.headingBold}>WHEREAS</Text> the Tenant has approached the Landlord for <Text style={docStyles.keyword}>{pr.roomDetails || ''}</Text> bearing
            Room No. <Text style={docStyles.keyword}>{pr.RoomNmber || ''}</Text> in <Text style={docStyles.keyword}>{pr.PGName || ''}</Text> for residential purpose only.
          </Clause>
        )}

        <Clause><Text style={docStyles.headingBold}>NOW THIS AGREEMENT WITNESSETH AS FOLLOWS:</Text></Clause>

        {/* Section 1: TENANCY TERM */}
        <Section>{num()}. TENANCY TERM</Section>
        <Clause>{sectionCount}.1 The Landlord agrees to let and the Tenant agrees to take on rent the said room for a period of <Text style={docStyles.keyword}>{tc.durationMonths || 11} months</Text> commencing
          from <Text style={docStyles.keyword}>{tc.startDate || ''}</Text> and ending on <Text style={docStyles.keyword}>{tc.endDate || ''}</Text>.</Clause>
        <Clause>{sectionCount}.2 There shall be a lock-in period of <Text style={docStyles.keyword}>{tc.lockInMonths || 6} months</Text> from the commencement date during which neither party
          can terminate the agreement except in case of breach.</Clause>
        <Clause>{sectionCount}.3 The agreement may be renewed for a further period on mutually agreed terms by giving a written notice at least{' '}
          <Text style={docStyles.keyword}>{tc.renewalNoticeDays || 30} days</Text> before expiry.</Clause>

        {/* Section 2: RENT & PAYMENT */}
        <Section>{num()}. RENT & PAYMENT TERMS</Section>
        <Clause>{sectionCount}.1 The Tenant shall pay a monthly rent of <Rupee /><Text style={docStyles.keyword}>{rn.monthlyRent || 0}</Text> (Rupees{' '}
          <Text style={docStyles.keyword}>{rn.monthlyRentWords || ''}</Text> only) to the Landlord.</Clause>
        <Clause>{sectionCount}.2 The rent shall be payable on or before the <Text style={docStyles.keyword}>{rn.dueDay || '5th'}</Text> day of every succeeding calendar month.</Clause>
        <Clause>{sectionCount}.3 Payment shall be made through <Text style={docStyles.keyword}>{rn.paymentMode || 'UPI/Cash'}</Text> to the Landlord's designated account.</Clause>
        <Clause>{sectionCount}.4 A late payment penalty of <Rupee /><Text style={docStyles.keyword}>{rn.latePenalty || 100}</Text> per day shall be charged for any delay beyond the due date.</Clause>
        <Clause>{sectionCount}.5 The rent shall be subject to an escalation of <Text style={docStyles.keyword}>{rn.escalationPercent || 0}%</Text> after every{' '}
          <Text style={docStyles.keyword}>{rn.escalationMonths || 12} months</Text> upon renewal.</Clause>

        {/* Section 3: SECURITY DEPOSIT */}
        <Section>{num()}. SECURITY DEPOSIT</Section>
        <Clause>{sectionCount}.1 The Tenant has paid a sum of <Rupee /><Text style={docStyles.keyword}>{dp.amount || 0}</Text> (Rupees{' '}
          <Text style={docStyles.keyword}>{dp.amountWords || ''}</Text> only) as interest-free refundable security deposit.</Clause>
        <Clause>{sectionCount}.2 The deposit shall be refunded within <Text style={docStyles.keyword}>{dp.refundDays || 15} days</Text> after the Tenant vacates the premises,
          subject to deduction for any damages, unpaid rent, or dues.</Clause>

        {/* Section 4: UTILITIES */}
        <Section>{num()}. UTILITIES & MAINTENANCE</Section>
        <Clause>{sectionCount}.1 The following are included in the rent: <Text style={docStyles.keyword}>{ut.included || ''}</Text></Clause>
        <Clause>{sectionCount}.2 The following shall be paid extra by the Tenant: <Text style={docStyles.keyword}>{ut.extra || ''}</Text></Clause>
        <Clause>{sectionCount}.3 Electricity and other metered charges beyond the included limit will be calculated as per actual sub-meter reading and paid by the Tenant within 7 days of intimation.</Clause>

        {/* Section 5: FOOD */}
        <Section>{num()}. FOOD / MEALS (if applicable)</Section>
        <Clause>{sectionCount}.1 The Landlord shall provide <Text style={docStyles.keyword}>{fd.included || ''}</Text> as per the standard menu on all days except{' '}
          <Text style={docStyles.keyword}>{fd.offDay || 'Sunday'}</Text>.</Clause>
        <Clause>{sectionCount}.2 Extra meals or special requests will be charged additionally at the rate fixed by the Landlord.</Clause>

        {/* Sections 6-8: RULES */}
        {ruleSections.map(s => (
          <RuleSection key={s.key} number={num()} title={s.title} intro={s.intro} items={s.items} />
        ))}

        {/* Section 9: TERMINATION */}
        <Section>{num()}. TERMINATION & NOTICE PERIOD</Section>
        <Clause>{sectionCount}.1 Either party may terminate the agreement by giving <Text style={docStyles.keyword}>{tm.noticeDays || '15 Days'}</Text> written notice after the lock-in period.</Clause>
        <Clause>{sectionCount}.2 In case of breach of any terms, the Landlord may terminate the agreement immediately and forfeit the security deposit.</Clause>
        <Clause>{sectionCount}.3 Upon termination, the Tenant must vacate the premises and clear all dues.</Clause>

        {/* Section 10: DEFAULT & BREACH */}
        <Section>{num()}. DEFAULT & BREACH</Section>
        <Clause>In case of default in payment of rent for two consecutive months or repeated violation of house rules, the Landlord shall have the right to evict
          the Tenant as per applicable law.</Clause>

        {/* Section 11: FORCE MAJEURE */}
        <Section>{num()}. FORCE MAJEURE</Section>
        <Clause>Neither party shall be liable for any delay or failure to perform obligations due to events beyond their control such as acts of God, war, riots,
          strikes, etc.</Clause>

        {/* Section 12: DISPUTE RESOLUTION */}
        <Section>{num()}. DISPUTE RESOLUTION</Section>
        <Clause>Any dispute arising out of this agreement shall be subject to the exclusive jurisdiction of the courts at <Text style={docStyles.keyword}>{d.city || 'Delhi'}</Text>.
          The parties may first attempt amicable settlement.</Clause>

        {/* Section 13: GOVERNING LAW */}
        <Section>{num()}. GOVERNING LAW</Section>
        <Clause>This Agreement shall be governed by the laws of India and the state of <Text style={docStyles.keyword}>{d.state || 'Delhi'}</Text>.</Clause>

        {/* Section 14: MISCELLANEOUS */}
        <Section>{num()}. MISCELLANEOUS</Section>
        <Clause>{sectionCount}.1 This Agreement constitutes the entire understanding between the parties.</Clause>
        <Clause>{sectionCount}.2 Any modification must be made in writing and signed by both parties.</Clause>
        <Clause>{sectionCount}.3 Notices shall be sent to the addresses mentioned above.</Clause>

        <Clause>IN WITNESS WHEREOF, the parties have set their hands on the day and year first above written.</Clause>

        {/* Signature Section */}
        <View style={docStyles.signBlockWrap}>
          <View style={docStyles.signRow}>
            <View style={docStyles.signCol}>
              <Text style={docStyles.signLabel}>Landlord</Text>
              <Text style={docStyles.signSub}>Signature: {lo.title || 'Mr.'} {lo.name || ''}</Text>
              <View style={docStyles.signLine} />
              <Text style={docStyles.signSub}>Name: <Text style={docStyles.keyword}>{lo.title || 'Mr.'} {lo.name || ''}</Text></Text>
              <Text style={docStyles.signSub}>Date: <Text style={docStyles.keyword}>{d.agreementDate}</Text></Text>
            </View>
            <View style={docStyles.signCol}>
              <Text style={docStyles.signLabel}>Tenant</Text>
              <Text style={docStyles.signSub}>Signature: 
                {isVerified ? (
                  <Text> {tn.name || ''}</Text>
                ) : (
                  <Text> </Text>
                )}
              </Text>
              <View style={docStyles.signLine} />
              <Text style={docStyles.signSub}>Name: <Text style={docStyles.keyword}>{tn.Title || 'Mr.'} {tn.name || ''}</Text></Text>
              <Text style={docStyles.signSub}>Date: <Text style={docStyles.keyword}>{d.agreementDate}</Text></Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// ═════════════════════════════════════════════════════════════════
// TENANT AGREEMENT MODAL VIEW - Main Component
// ═════════════════════════════════════════════════════════════════

const TenantAgreementModalView = ({ 
  isOpen, 
  onClose, 
  pgId, 
  tenantId,
  agreementStatus = 'DRAFT',
  statusText = 'Draft',
  statusColor = '#F59E0B',
  showSaveToCloud = false,
  onUploadSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  // Check if mobile
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
  }, []);


   const handleOpenPDF = async () => {
      const { pdf } = await import('@react-pdf/renderer');
      const blob = await pdf(<RentAgreementDocument data={agData} isVerified={isVerified} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    };
    
  // Fetch agreement data when modal opens
  useEffect(() => {
    if (isOpen && pgId && tenantId) {
      fetchAgreementData();
    }
  }, [isOpen, pgId, tenantId]);

  const fetchAgreementData = async () => {
    if (!pgId || !tenantId) {
      setError('PG ID and Tenant ID are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getAgreementJsonData(pgId, tenantId);
      console.log('Agreement API Response:', response);

      if (response.success && response.data) {
        let parsedData = response.data;
        
        // Parse agreementjson if it's a string
        if (typeof response.data.agreementjson === 'string') {
          try {
            const jsonData = JSON.parse(response.data.agreementjson);
            if (Array.isArray(jsonData) && jsonData.length > 0) {
              parsedData = {
                ...response.data,
                agreementjson: jsonData[0]
              };
            } else {
              parsedData = {
                ...response.data,
                agreementjson: jsonData
              };
            }
          } catch (parseError) {
            console.error('Error parsing agreement JSON:', parseError);
            parsedData = response.data;
          }
        } else if (Array.isArray(response.data.agreementjson)) {
          parsedData = {
            ...response.data,
            agreementjson: response.data.agreementjson[0] || response.data.agreementjson
          };
        }

        setAgreementData(parsedData);
      } else {
        setError(response.message || 'Failed to fetch agreement data');
      }
    } catch (err) {
      console.error('Error fetching agreement:', err);
      setError(err.message || 'An error occurred while fetching agreement');
    } finally {
      setLoading(false);
    }
  };

  // ─── Upload to Cloud ──────────────────────────────────────────────
  const handleGenerateAndUpload = async () => {
    const agreementId = agreementData?.AgreementID;
    
    if (!agreementId) {
      alert('Agreement ID is required to save the document.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadStatus('generating');
      setUploadError(null);

      // Generate PDF as blob
      const pdfBlob = await pdf(
        <RentAgreementDocument data={agreementData?.agreementjson} isVerified={agreementStatus === 'TENANT_ACCEPTED'} />
      ).toBlob();

      // Create File object
      const file = new File(
        [pdfBlob],
        `agreement_${agreementId}_${Date.now()}.pdf`,
        { type: 'application/pdf' }
      );

      setUploadStatus('uploading');

      // Call API to upload
      const result = await generateAgreementPDF(
        agreementId,
        file
      );

      if (result.success) {
        setUploadStatus('success');
        console.log('✅ PDF uploaded successfully:', result.data);
        
        if (onUploadSuccess) {
          onUploadSuccess(result.data);
        }
      } else {
        setUploadStatus('error');
        setUploadError(result.message || 'Failed to upload PDF');
      }
    } catch (error) {
      console.error('Error:', error);
      setUploadStatus('error');
      setUploadError(error.message || 'An error occurred while uploading');
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Close handler ──────────────────────────────────────────────
  const handleClose = () => {
    setError(null);
    setUploadStatus(null);
    setUploadError(null);
    onClose();
  };

  // ─── Render ──────────────────────────────────────────────────────
  if (!isOpen) return null;

  const agData = agreementData?.agreementjson || null;
  const tenantName = agData?.tenant?.[0]?.name || agData?.tenant?.name || 'Tenant';
  const roomNumber = agData?.property?.[0]?.RoomNmber || agData?.property?.RoomNmber || 'TBD';
  const agreementId = agreementData?.AgreementID;
  const isVerified = agreementStatus === 'TENANT_ACCEPTED';

  return (
    <div className="va-overlay" onClick={handleClose}>
      <div className="va-modal" onClick={e => e.stopPropagation()}>
        {/* ─── Modal Header ─────────────────────────────────────── */}
        <div className="va-modal-head">
          <div>
            <h3 className="va-modal-title">Rent Agreement</h3>
            <p className="va-modal-sub">
              {tenantName} · Room {roomNumber}
              {agreementStatus && (
                <span style={{ 
                  marginLeft: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: statusColor,
                  padding: '2px 10px',
                  borderRadius: '12px',
                  backgroundColor: `${statusColor}15`,
                  border: `1px solid ${statusColor}30`,
                }}>
                  {statusText}
                </span>
              )}
            </p>
          </div>

          <div className="va-modal-actions">
            {/* Save to Cloud Button */}
            {agreementId && showSaveToCloud && (
              <button 
                className="va-upload-btn" 
                onClick={handleGenerateAndUpload}
                disabled={isUploading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isUploading ? '#9CA3AF' : '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                }}
              >
                {isUploading ? (
                  <>
                    <span className="spinner" style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid white',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    {uploadStatus === 'generating' ? 'Generating...' : 'Uploading...'}
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                    </svg>
                    Save to Cloud
                  </>
                )}
              </button>
            )}

            {isMobile && ( 
              <button className="va-download-btn" onClick={handleOpenPDF} style={{ marginRight: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                </svg>
                Open PDF
              </button>
            )}
            
            {agData && (
              <PDFDownloadLink
                document={<RentAgreementDocument data={agData} isVerified={isVerified} />}
                fileName={`Agreement_${agreementId || 'Rent'}.pdf`}
                className="va-download-btn"
              >
                {({ loading }) => (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                    </svg>
                    {loading ? 'Preparing…' : 'Download PDF'}
                  </>
                )}
              </PDFDownloadLink>
            )}

            <button className="va-modal-close" onClick={handleClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* ─── Upload Status Messages ──────────────────────────── */}
        {uploadStatus === 'success' && (
          <div style={{
            padding: '10px 20px',
            backgroundColor: '#D1FAE5',
            color: '#065F46',
            borderBottom: '1px solid #A7F3D0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>✅</span>
            PDF uploaded successfully to cloud storage
          </div>
        )}
        {uploadStatus === 'error' && (
          <div style={{
            padding: '10px 20px',
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            borderBottom: '1px solid #FCA5A5',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>❌</span>
            {uploadError || 'Failed to upload PDF. Please try again.'}
          </div>
        )}

        {/* ─── Modal Body ────────────────────────────────────────── */}
        <div className="va-modal-body">
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '16px'
            }}>
              <div className="spinner" style={{
                width: '40px',
                height: '40px',
                border: '3px solid #E5E7EB',
                borderTop: '3px solid #F06E18',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#6B7280' }}>Loading agreement...</p>
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '40px',
              textAlign: 'center'
            }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <h3 style={{ marginTop: '16px', color: '#1F2937' }}>Unable to Load Agreement</h3>
              <p style={{ color: '#6B7280', maxWidth: '400px' }}>{error}</p>
              <button 
                onClick={fetchAgreementData}
                style={{
                  marginTop: '16px',
                  padding: '8px 24px',
                  backgroundColor: '#F06E18',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          ) : !agData ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '40px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#6B7280' }}>No agreement data available</p>
            </div>
          ) : isMobile ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              backgroundColor: '#f9fafb',
              padding: '20px',
              textAlign: 'center'
            }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#f06e18" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6M12 18v-6M9 15l3-3 3 3" />
              </svg>
              <h3 style={{ marginTop: '16px', color: '#111827', fontSize: '18px' }}>
                Rent Agreement
              </h3>
              <p style={{ color: '#6b7280', maxWidth: '300px', fontSize: '14px' }}>
                Tap <strong>"Open PDF"</strong> above to view your agreement in a new tab.
              </p>
              <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>
                {tenantName} · Room {roomNumber}
                {isVerified && (
                  <span style={{ 
                    marginLeft: '8px', 
                    color: '#059669',
                    fontWeight: '600'
                  }}>
                    ✓ Verified
                  </span>
                )}
              </p>
            </div>
          ) : (
            <PDFViewer width="100%" height="100%" showToolbar={false}>
              <RentAgreementDocument data={agData} isVerified={isVerified} />
            </PDFViewer>
          )}
        </div>
      </div>
    </div>
  );
};

// Add spinner animation CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default TenantAgreementModalView;